// src/context/ExperienceContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { getExperiences } from '../services/contentful';
import { calculateYearsOfExperience } from '../utils/experience';

const ExperienceContext = createContext();

// Fallback mock experiences (same as in Experience.jsx)
const mockExperiences = [
  {
    id: 'mock-1',
    jobTitle: 'Backend Developer',
    company: 'Wealth Gambit Media',
    companyUrl: 'https://wgmedia.com',
    employmentType: 'Full-time',
    location: 'Remote',
    startDate: '2025-06-01',
    endDate: null,
    isCurrent: true,
    description: 'Currently developing robust backend systems and APIs for media platforms.',
    responsibilities: [],
    technologies: ['Node.js', 'Express.js', 'MongoDB', 'PostgreSQL', 'Docker', 'AWS'],
    achievements: [],
    displayOrder: 1
  },
  {
    id: 'mock-2',
    jobTitle: 'Quality Assurance Intern',
    company: 'Tech Innovation Corp',
    companyUrl: '#',
    employmentType: 'Internship',
    location: 'On-site',
    startDate: '2025-02-01',
    endDate: '2025-05-31',
    isCurrent: false,
    description: 'Gained valuable experience in software testing methodologies.',
    responsibilities: [],
    technologies: ['Selenium', 'Jest', 'Cypress', 'Postman', 'JIRA', 'TestRail'],
    achievements: [],
    displayOrder: 2
  },
  {
    id: 'mock-3',
    jobTitle: 'Freelance Full Stack Developer',
    company: 'Independent Contractor',
    companyUrl: '#',
    employmentType: 'Freelance',
    location: 'Remote',
    startDate: '2024-09-01',
    endDate: '2025-01-31',
    isCurrent: false,
    description: 'Provided end-to-end web development services to various clients.',
    responsibilities: [],
    technologies: ['React.js', 'Node.js', 'MongoDB', 'PostgreSQL', 'Firebase', 'Vercel'],
    achievements: [],
    displayOrder: 3
  },
  {
    id: 'mock-4',
    jobTitle: 'Backend Developer',
    company: 'withKM',
    companyUrl: 'https://withkm.com',
    employmentType: 'Full-time',
    location: 'Remote',
    startDate: '2022-01-01',
    endDate: '2024-08-31',
    isCurrent: false,
    description: 'Co-founded and developed the backend infrastructure for a comprehensive business platform.',
    responsibilities: [],
    technologies: ['Node.js', 'Express.js', 'MongoDB', 'Redis', 'Docker', 'AWS', 'GraphQL'],
    achievements: [],
    displayOrder: 4
  }
];

// Calculate unique companies count (excluding empty/null company names)
const calculateCompaniesCount = (experiences) => {
  const validCompanies = experiences
    .map(exp => exp.company)
    .filter(company => company && company.trim() !== '');
  return new Set(validCompanies).size;
};

export const ExperienceProvider = ({ children }) => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yearsOfExperience, setYearsOfExperience] = useState({
    years: 0,
    months: 0,
    totalMonths: 0,
    displayText: '0+',
    fullDisplayText: '0 Months',
    startDate: null
  });
  const [companiesCount, setCompaniesCount] = useState(0);

  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        setLoading(true);
        setError(null);

        const contentfulExperiences = await getExperiences();

        const expData = contentfulExperiences.length > 0
          ? contentfulExperiences
          : mockExperiences;

        if (contentfulExperiences.length === 0) {
          console.warn('No experiences found in Contentful, using fallback data');
        }

        setExperiences(expData);
        setYearsOfExperience(calculateYearsOfExperience(expData));
        setCompaniesCount(calculateCompaniesCount(expData));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching experiences:', err);
        setError('Failed to load experience data.');
        // Use mock data on error
        setExperiences(mockExperiences);
        setYearsOfExperience(calculateYearsOfExperience(mockExperiences));
        setCompaniesCount(calculateCompaniesCount(mockExperiences));
        setLoading(false);
      }
    };

    fetchExperiences();
  }, []);

  return (
    <ExperienceContext.Provider value={{
      experiences,
      loading,
      error,
      yearsOfExperience,
      companiesCount
    }}>
      {children}
    </ExperienceContext.Provider>
  );
};

export const useExperience = () => {
  const context = useContext(ExperienceContext);
  if (!context) {
    throw new Error('useExperience must be used within an ExperienceProvider');
  }
  return context;
};

export default ExperienceContext;
